import { useCallback, useContext, useEffect, useState } from "react";
import { App, Alert, Button, DatePicker, Flex, Input, Modal, Select, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { CompanyContext, UpdateCompanyContext } from "../../context/companyContext";
import { downloadApiKeyCsv } from "../../utils/apiKeyExport";
import {
  ApiKeyProvider,
  ExternalApiKeyListItem,
  ExternalApiKeyStatusFilter,
  IssuedApiKey,
  canIssueApiKey,
  useApiKeys,
} from "../../context/apiKeyContext";
import { ApiKeyEventsProvider } from "../../context/apiKeyEventsContext";
import EventsTab from "./EventsTab";
import { useTranslation } from "react-i18next";

function fmt(value: string | null): string {
  return value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-";
}

function ExternalApiKeyPageInner({
  companyId,
  setCompanyId,
}: {
  companyId: number | null;
  setCompanyId: (id: number) => void;
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { companyOptions } = useContext(CompanyContext);
  const { refreshCompanyOptions } = useContext(UpdateCompanyContext);
  const { keys, pagination, limits, activeCount, loading, refreshKeys, refreshLimits, createKey, rotateKey, revokeKey } =
    useApiKeys();

  const [statusFilter, setStatusFilter] = useState<ExternalApiKeyStatusFilter>("active");

  // 発行モーダル
  const [issueOpen, setIssueOpen] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issueName, setIssueName] = useState("");
  const [issueExpiresAt, setIssueExpiresAt] = useState<dayjs.Dayjs | null>(null);
  const [issueAllowedIps, setIssueAllowedIps] = useState("");

  // 平文キー一度きり表示
  const [issuedKey, setIssuedKey] = useState<IssuedApiKey | null>(null);

  // 失効モーダル
  const [revokeTarget, setRevokeTarget] = useState<ExternalApiKeyListItem | null>(null);
  const [revokeReason, setRevokeReason] = useState("");

  useEffect(() => {
    refreshCompanyOptions();
  }, [refreshCompanyOptions]);

  const reload = useCallback(
    (cid: number, status: ExternalApiKeyStatusFilter) => {
      refreshKeys(cid, status, 1, 20);
      refreshLimits(cid);
    },
    [refreshKeys, refreshLimits]
  );

  const onSelectCompany = useCallback(
    (cid: number) => {
      setCompanyId(cid);
      reload(cid, statusFilter);
    },
    [reload, statusFilter, setCompanyId]
  );

  const onChangeStatus = useCallback(
    (status: ExternalApiKeyStatusFilter) => {
      setStatusFilter(status);
      if (companyId != null) refreshKeys(companyId, status, 1, 20);
    },
    [companyId, refreshKeys]
  );

  const planLimit = limits?.maxApiKeysPerCompany ?? null;
  const issuable = companyId != null && canIssueApiKey(activeCount, planLimit);

  const openIssue = useCallback(() => {
    setIssueName("");
    setIssueExpiresAt(null);
    setIssueAllowedIps("");
    setIssueOpen(true);
  }, []);

  const submitIssue = useCallback(async () => {
    if (companyId == null) return;
    if (!issueName.trim()) {
      message.error(t("externalApiKey.validation.nameRequired"));
      return;
    }
    setIssuing(true);
    try {
      const allowedIps = limits?.allowIpRestriction && issueAllowedIps.trim() ? issueAllowedIps.trim() : null;
      const result = await createKey(companyId, {
        name: issueName.trim(),
        expires_at: issueExpiresAt ? issueExpiresAt.toISOString() : null,
        allowed_ips: allowedIps,
      });
      if (!result.ok) {
        message.error(result.message);
        return;
      }
      setIssueOpen(false);
      setIssuedKey(result.key); // 平文キーを一度きり表示
      reload(companyId, statusFilter);
    } finally {
      setIssuing(false);
    }
  }, [companyId, issueName, issueExpiresAt, issueAllowedIps, limits, createKey, message, reload, statusFilter, t]);

  const onRotate = useCallback(
    (row: ExternalApiKeyListItem) => {
      Modal.confirm({
        title: t("externalApiKey.rotateConfirmTitle"),
        content: t("externalApiKey.rotateConfirmContent", { name: row.name }),
        okText: t("externalApiKey.rotate"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          const result = await rotateKey(row.id);
          if (!result.ok) {
            message.error(result.message);
            return;
          }
          setIssuedKey(result.key);
          if (companyId != null) reload(companyId, statusFilter);
        },
      });
    },
    [rotateKey, message, companyId, reload, statusFilter, t]
  );

  const submitRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    const result = await revokeKey(revokeTarget.id, revokeReason.trim() || null);
    if (!result.ok) {
      message.error(result.message ?? t("externalApiKey.revokeFailed"));
      return;
    }
    message.success(t("externalApiKey.revokeSucceeded"));
    setRevokeTarget(null);
    setRevokeReason("");
    if (companyId != null) reload(companyId, statusFilter);
  }, [revokeTarget, revokeReason, revokeKey, message, companyId, reload, statusFilter, t]);

  const columns: ColumnsType<ExternalApiKeyListItem> = [
    { title: t("externalApiKey.columns.name"), dataIndex: "name", key: "name" },
    { title: t("externalApiKey.columns.keyPrefix"), dataIndex: "key_prefix", key: "key_prefix", render: (v) => <code>{v}</code> },
    {
      title: t("common.status"),
      dataIndex: "status",
      key: "status",
      render: (s: string) => <Tag color={s === "active" ? "green" : "red"}>{s === "active" ? t("common.active") : t("externalApiKey.revoked")}</Tag>,
    },
    { title: t("externalApiKey.columns.expiresAt"), dataIndex: "expires_at", key: "expires_at", render: fmt },
    { title: t("externalApiKey.columns.lastUsedAt"), dataIndex: "last_used_at", key: "last_used_at", render: fmt },
    { title: t("externalApiKey.columns.createdAt"), dataIndex: "created_at", key: "created_at", render: fmt },
    { title: t("externalApiKey.columns.revokedAt"), dataIndex: "revoked_at", key: "revoked_at", render: fmt },
    {
      title: t("common.operation"),
      key: "actions",
      render: (_: unknown, row: ExternalApiKeyListItem) =>
        row.status === "active" ? (
          <Flex gap="small">
            <Button size="small" onClick={() => onRotate(row)}>
              {t("externalApiKey.rotate")}
            </Button>
            <Button size="small" danger onClick={() => setRevokeTarget(row)}>
              {t("externalApiKey.revoke")}
            </Button>
          </Flex>
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
    },
  ];

  return (
    <div>
      <Flex align="center" gap="middle" wrap style={{ marginBottom: 16 }}>
        <Select
          showSearch
          placeholder={t("company.selectCompany")}
          style={{ minWidth: 280 }}
          optionFilterProp="label"
          value={companyId ?? undefined}
          onChange={onSelectCompany}
          options={companyOptions.map((c) => ({ label: c.company_name, value: c.id }))}
        />
        {companyId != null && (
          <>
            <span>
              {t("externalApiKey.keyCount")}: <b>{activeCount}</b> / {planLimit ?? "-"}
            </span>
            <Select
              value={statusFilter}
              style={{ width: 140 }}
              onChange={onChangeStatus}
              options={[
                { label: t("externalApiKey.filters.activeOnly"), value: "active" },
                { label: t("externalApiKey.filters.revokedOnly"), value: "revoked" },
                { label: t("common.all"), value: "all" },
              ]}
            />
            <Tooltip
              title={
                issuable
                  ? ""
                  : t("externalApiKey.planLimitReached")
              }
            >
              <Button type="primary" disabled={!issuable} onClick={openIssue}>
                {t("externalApiKey.issue")}
              </Button>
            </Tooltip>
          </>
        )}
      </Flex>

      {companyId != null && (
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={keys}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page, pageSize) => companyId != null && refreshKeys(companyId, statusFilter, page, pageSize),
          }}
        />
      )}

      {/* 発行モーダル */}
      <Modal
        title={t("externalApiKey.issue")}
        open={issueOpen}
        onOk={submitIssue}
        confirmLoading={issuing}
        onCancel={() => setIssueOpen(false)}
        okText={t("externalApiKey.issueOk")}
        cancelText={t("common.cancel")}
      >
        <Flex vertical gap="middle" style={{ marginTop: 12 }}>
          <div>
            <div style={{ marginBottom: 4 }}>{t("externalApiKey.fields.nameRequired")}</div>
            <Input value={issueName} onChange={(e) => setIssueName(e.target.value)} placeholder={t("externalApiKey.placeholders.name")} maxLength={255} />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>{t("externalApiKey.fields.expiresAtOptional")}</div>
            <DatePicker
              style={{ width: "100%" }}
              showTime
              value={issueExpiresAt}
              onChange={setIssueExpiresAt}
              placeholder={t("externalApiKey.placeholders.noExpiry")}
            />
          </div>
          {limits?.allowIpRestriction && (
            <div>
              <div style={{ marginBottom: 4 }}>{t("externalApiKey.fields.allowedIpsOptional")}</div>
              <Input
                value={issueAllowedIps}
                onChange={(e) => setIssueAllowedIps(e.target.value)}
                placeholder={t("externalApiKey.placeholders.allowedIps")}
              />
            </div>
          )}
        </Flex>
      </Modal>

      {/* 平文キー一度きり表示 */}
      <Modal
        title={t("externalApiKey.issuedTitle")}
        open={issuedKey !== null}
        onCancel={() => setIssuedKey(null)}
        footer={[
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={() => issuedKey && downloadApiKeyCsv(issuedKey)}
          >
            {t("common.downloadCsv")}
          </Button>,
          <Button key="done" type="primary" onClick={() => setIssuedKey(null)}>
            {t("common.close")}
          </Button>,
        ]}
      >
        <Alert type="warning" showIcon message={t("externalApiKey.fullKeyWarning")} style={{ marginBottom: 12 }} />
        <Typography.Paragraph copyable={{ text: issuedKey?.full_key }} style={{ wordBreak: "break-all" }}>
          <code>{issuedKey?.full_key}</code>
        </Typography.Paragraph>
        <div style={{ color: "#666" }}>
          {t("externalApiKey.issuedNameHelp", { name: issuedKey?.name })}
        </div>
      </Modal>

      {/* 失効モーダル */}
      <Modal
        title={t("externalApiKey.revokeTitle")}
        open={revokeTarget !== null}
        onOk={submitRevoke}
        onCancel={() => {
          setRevokeTarget(null);
          setRevokeReason("");
        }}
        okText={t("externalApiKey.revoke")}
        okButtonProps={{ danger: true }}
        cancelText={t("common.cancel")}
      >
        <p>
          {t("externalApiKey.revokeContent", { name: revokeTarget?.name })}
        </p>
        <div style={{ marginBottom: 4 }}>{t("externalApiKey.revokeReasonOptional")}</div>
        <Input.TextArea
          value={revokeReason}
          onChange={(e) => setRevokeReason(e.target.value)}
          maxLength={255}
          rows={2}
          placeholder={t("externalApiKey.placeholders.revokeReason")}
        />
      </Modal>
    </div>
  );
}

/** タブ構成 (キー一覧 + 操作履歴)。企業セレクタ(companyId)はタブ間で共有する。 */
function ExternalApiKeyPageOuter() {
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState<number | null>(null);
  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>{t("externalApiKey.title")}</Typography.Title>
      <Tabs
        defaultActiveKey="keys"
        items={[
          {
            key: "keys",
            label: t("externalApiKey.tabs.keys"),
            children: <ExternalApiKeyPageInner companyId={companyId} setCompanyId={setCompanyId} />,
          },
          {
            key: "events",
            label: t("externalApiKey.tabs.events"),
            children: <EventsTab companyId={companyId} />,
          },
        ]}
      />
    </div>
  );
}

export default function ExternalApiKeyPage() {
  return (
    <ApiKeyProvider>
      <ApiKeyEventsProvider>
        <ExternalApiKeyPageOuter />
      </ApiKeyEventsProvider>
    </ApiKeyProvider>
  );
}
